// src/pages/HousekeeperDashboard.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import {
  Table,
  Card,
  Button,
  Space,
  Typography,
  Tag,
  Badge,
  Modal,
  notification,
  Breadcrumb,
  Alert,
  Input,
  Select,
  Divider,
  List,
  Tooltip,
  Spin,
  Progress,
  Descriptions,
  Form,
} from "antd";

import {
  FaTools,
  FaClock,
  FaCheckCircle,
  FaTimes,
  FaSearch,
  FaInfoCircle,
  FaUserPlus,
  FaSync,
  FaListUl,
} from "react-icons/fa";

import housekeepingApi from "../api/housekeepingApi";

const { Title, Text } = Typography;
const { Option } = Select;

const HOUSEKEEPING_TASK_STATUS = {
  Pending: "Pending",
  InProgress: "InProgress",
  Completed: "Completed",
  Cancelled: "Cancelled",
};

const statusTag = (status) => {
  const config =
    {
      Pending: { color: "orange", icon: <FaClock /> },
      InProgress: { color: "blue", icon: <FaTools /> },
      Completed: { color: "green", icon: <FaCheckCircle /> },
      Cancelled: { color: "red", icon: <FaTimes /> },
    }[status] || { color: "default", icon: null };

  return (
    <Tag color={config.color} icon={config.icon}>
      {status || "Unknown"}
    </Tag>
  );
};

const taskTitle = (t) =>
  (t?.title && t.title.trim()) ||
  `${String(t?.taskType || "Cleaning")
    .replace(/([A-Z])/g, " $1")
    .trim()} — Room ${t?.roomNumber ?? t?.roomId ?? "N/A"}`;

const calcChecklistProgress = (task) => {
  const items = Array.isArray(task?.checklistItems) ? task.checklistItems : [];
  const total = items.length;
  const done = items.filter((x) => !!x?.isCompleted).length;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  return { total, done, percent };
};

const HousekeeperDashboard = () => {
  const { authData } = useAuth();
  const navigate = useNavigate();

  const hotelId = authData?.hotelId;

  // data
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // assign
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignTaskModalVisible, setAssignTaskModalVisible] = useState(false);
  const [assignTaskForm] = Form.useForm();

  // details (read-only)
  const [currentTask, setCurrentTask] = useState(null);
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [taskDetailsLoading, setTaskDetailsLoading] = useState(false);

  // filters
  const [filters, setFilters] = useState({ status: null, search: "" });

  const breadcrumbItems = [
    { title: <Link to="/dashboard">Home</Link> },
    { title: "Housekeeper Dashboard" },
  ];

  const fetchAssignableUsers = useCallback(async () => {
    if (!hotelId) return;

    setLoadingUsers(true);
    try {
      const response = await housekeepingApi.getAssignableUsers(hotelId);
      setAssignableUsers(response.data || []);
    } catch (error) {
      // Soft warning for 404
      if (error?.response?.status === 404) {
        notification.info({
          title: "No Staff Available",
          description:
            "No users with housekeeping permissions found. You can still view tasks, but assignment will be unavailable until permissions are configured.",
          duration: 4,
          placement: "topRight",
        });
      } else {
        notification.error({
          title: "Error Loading Staff",
          description: error?.response?.data?.message || "Failed to load assignable users",
          duration: 4,
        });
      }
      setAssignableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [hotelId]);

  const fetchTasks = useCallback(async () => {
    if (!hotelId) {
      setTasks([]);
      setFilteredTasks([]);
      setLoading(false);
      notification.error({
        title: "Hotel context missing",
        description: "Please log in again.",
      });
      return;
    }

    setLoading(true);
    try {
      // Use ALL tasks for assignment + monitoring.
      const response = await housekeepingApi.getAllTasks(hotelId);
      const fetched = Array.isArray(response?.data) ? response.data : [];
      setTasks(fetched);
      setFilteredTasks(fetched);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      notification.error({
        title: "Error",
        description: error?.response?.data?.message || "Failed to load tasks",
      });
      setTasks([]);
      setFilteredTasks([]);
    } finally {
      setLoading(false);
    }
  }, [hotelId]);

  useEffect(() => {
    (async () => {
      await Promise.all([fetchTasks(), fetchAssignableUsers()]);
    })();
  }, [fetchTasks, fetchAssignableUsers]);

  const stats = useMemo(() => {
    const s = { pending: 0, inProgress: 0, completed: 0, cancelled: 0 };
    for (const t of tasks) {
      if (t.status === HOUSEKEEPING_TASK_STATUS.Pending) s.pending++;
      else if (t.status === HOUSEKEEPING_TASK_STATUS.InProgress) s.inProgress++;
      else if (t.status === HOUSEKEEPING_TASK_STATUS.Completed) s.completed++;
      else if (t.status === HOUSEKEEPING_TASK_STATUS.Cancelled) s.cancelled++;
    }
    return s;
  }, [tasks]);

  useEffect(() => {
    const q = (filters.search || "").trim().toLowerCase();
    const st = filters.status;

    const next = tasks.filter((t) => {
      const statusOk = !st || t.status === st;

      const text = `${taskTitle(t)} ${t.description || ""} ${t.roomNumber || ""}`.toLowerCase();
      const searchOk = !q || text.includes(q);

      return statusOk && searchOk;
    });

    setFilteredTasks(next);
  }, [tasks, filters]);

  const openTaskDetails = async (task) => {
    const id = task?.housekeepingTaskId ?? task?.id;
    if (!id) return;

    setInfoModalVisible(true);
    setTaskDetailsLoading(true);
    setCurrentTask(null);

    try {
      // IMPORTANT: your controller GET requires hotelId query:
      // GET api/Housekeeping/{id}?hotelId=123
      const res = await housekeepingApi.getTaskById(id, hotelId);
      setCurrentTask(res?.data || task);
    } catch (e) {
      // fallback to row data, but checklistItems may be missing
      console.error("Failed to load task details:", e);
      setCurrentTask(task);
      notification.warning({
        title: "Partial details",
        description:
          e?.response?.data?.message ||
          "Could not load full task details. Showing what is available from the list.",
      });
    } finally {
      setTaskDetailsLoading(false);
    }
  };

  const openAssignModal = () => {
    assignTaskForm.resetFields();
    setAssignTaskModalVisible(true);
  };

  const handleAssignTaskSubmit = async () => {
    try {
      const v = await assignTaskForm.validateFields();

      await housekeepingApi.assignTask({
        taskId: v.taskId,
        userId: v.userId,
        hotelId,
      });

      notification.success({
        title: "Assigned",
        description: `Task #${v.taskId} assigned successfully.`,
      });

      setAssignTaskModalVisible(false);
      await fetchTasks();
    } catch (error) {
      if (error?.errorFields) return; // form validation
      notification.error({
        title: "Assignment failed",
        description: error?.response?.data?.message || error?.message || "Failed to assign task",
      });
    }
  };

  const columns = [
    {
      title: "Task ID",
      dataIndex: "housekeepingTaskId",
      key: "housekeepingTaskId",
      width: 100,
      sorter: (a, b) => (a.housekeepingTaskId || 0) - (b.housekeepingTaskId || 0),
      render: (id, record) => (
        <Button type="link" onClick={() => openTaskDetails(record)}>
          {id}
        </Button>
      ),
    },
    {
      title: "Title",
      key: "title",
      render: (_, record) => (
        <Button type="link" onClick={() => openTaskDetails(record)}>
          {taskTitle(record)}
        </Button>
      ),
    },
    {
      title: "Room",
      dataIndex: "roomNumber",
      key: "roomNumber",
      width: 120,
      render: (text) => text || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: statusTag,
      filters: Object.values(HOUSEKEEPING_TASK_STATUS).map((s) => ({ text: s, value: s })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Progress",
      key: "progress",
      width: 220,
      render: (_, record) => {
        const { done, total, percent } = calcChecklistProgress(record);

        // If your list endpoint does not include checklistItems, you'll see 0/0 here.
        // You can still view exact progress in the Details modal (it loads full task).
        if (!record.checklistItems) {
          return (
            <Tooltip title="Open details to view checklist progress">
              <Text type="secondary">Open details</Text>
            </Tooltip>
          );
        }

        return (
          <div style={{ minWidth: 180 }}>
            <Progress percent={percent} size="small" />
            <Text type="secondary">
              {done}/{total}
            </Text>
          </div>
        );
      },
    },
    {
      title: "Assigned At",
      dataIndex: "assignedAt",
      key: "assignedAt",
      width: 200,
      render: (date) => (date ? new Date(date).toLocaleString() : "N/A"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details (Read-only)">
            <Button icon={<FaInfoCircle />} onClick={() => openTaskDetails(record)} />
          </Tooltip>

          {record.status === HOUSEKEEPING_TASK_STATUS.Pending && (
            <Tooltip
              title={
                assignableUsers.length === 0
                  ? "No assignable users available"
                  : "Assign Task"
              }
            >
              <Button
                type="dashed"
                icon={<FaUserPlus />}
                onClick={() => {
                  openAssignModal();
                  assignTaskForm.setFieldsValue({ taskId: record.housekeepingTaskId });
                }}
                disabled={assignableUsers.length === 0}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={breadcrumbItems} style={{ marginBottom: 16 }} />

      <Card
        title={
          <Space>
            <Text strong>Housekeeping Assignments & Progress</Text>
            <Badge count={tasks.length} showZero style={{ backgroundColor: "#1890ff" }} />
          </Space>
        }
        extra={
          <Space>
            <Tooltip title="Checklist workspace (separate module)">
              <Button icon={<FaListUl />} onClick={() => navigate("/hotel/housekeeping/checklists")}>
                Checklists
              </Button>
            </Tooltip>

            <Button icon={<FaSync />} onClick={fetchTasks} loading={loading}>
              Refresh
            </Button>
          </Space>
        }
      >
        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 16 }}>
          <Card size="small"><Title level={5}>Pending</Title><Title level={2}>{stats.pending}</Title></Card>
          <Card size="small"><Title level={5}>In Progress</Title><Title level={2}>{stats.inProgress}</Title></Card>
          <Card size="small"><Title level={5}>Completed</Title><Title level={2}>{stats.completed}</Title></Card>
          <Card size="small"><Title level={5}>Cancelled</Title><Title level={2}>{stats.cancelled}</Title></Card>
        </div>

        {/* Filters */}
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search tasks..."
            prefix={<FaSearch />}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{ width: 300, marginRight: 16 }}
          />

          <Select
            placeholder="Filter by status"
            style={{ width: 180 }}
            allowClear
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
          >
            {Object.values(HOUSEKEEPING_TASK_STATUS).map((s) => (
              <Option key={s} value={s}>
                {s}
              </Option>
            ))}
          </Select>
        </div>

        {/* Assignment warning */}
        {assignableUsers.length === 0 && !loadingUsers && (
          <Alert
            message="No Staff Available for Assignment"
            description="No users with housekeeping permissions found. Configure permissions to enable assigning tasks."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" type="primary" onClick={() => navigate("/hotels/PermissionManager")}>
                Configure Permissions
              </Button>
            }
          />
        )}

        <Table
          rowKey="housekeepingTaskId"
          columns={columns}
          dataSource={filteredTasks}
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (total) => `Total ${total} tasks`,
          }}
          scroll={{ x: true }}
          locale={{
            emptyText: (
              <Space direction="vertical">
                <Text>No tasks found</Text>
                <Button icon={<FaSync />} onClick={fetchTasks}>
                  Refresh
                </Button>
              </Space>
            ),
          }}
        />

        {/* Assign Task Modal */}
        <Modal
          title="Assign Housekeeping Task"
          open={assignTaskModalVisible}
          onCancel={() => setAssignTaskModalVisible(false)}
          onOk={handleAssignTaskSubmit}
          confirmLoading={loadingUsers}
          width={700}
          destroyOnClose
        >
          {assignableUsers.length === 0 ? (
            <Alert
              message="No Staff Available"
              description="There are no users with housekeeping permissions. Please configure user permissions first."
              type="warning"
              showIcon
            />
          ) : (
            <Form form={assignTaskForm} layout="vertical">
              <Form.Item
                name="taskId"
                label="Select Task"
                rules={[{ required: true, message: "Please select a task" }]}
              >
                <Select placeholder="Select a task to assign" loading={loading}>
                  {tasks
                    .filter((t) => t.status === HOUSEKEEPING_TASK_STATUS.Pending)
                    .map((t) => (
                      <Option key={t.housekeepingTaskId} value={t.housekeepingTaskId}>
                        {taskTitle(t)} (Room: {t.roomNumber || "N/A"})
                      </Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="userId"
                label="Assign To"
                rules={[{ required: true, message: "Please select a user" }]}
              >
                <Select placeholder="Select staff member">
                  {assignableUsers.map((u) => (
                    <Option key={u.userId} value={u.userId}>
                      {u.firstName} {u.lastName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
          )}
        </Modal>

        {/* Details Modal (Read-only, includes checklist progress) */}
        <Modal
          title={`Task Details #${currentTask?.housekeepingTaskId || ""}`}
          open={infoModalVisible}
          onCancel={() => setInfoModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setInfoModalVisible(false)}>
              Close
            </Button>,
          ]}
          width={800}
        >
          {taskDetailsLoading ? (
            <div style={{ padding: 36, textAlign: "center" }}>
              <Spin />
            </div>
          ) : currentTask ? (
            <>
              <Descriptions bordered column={1}>
                <Descriptions.Item label="Title">{taskTitle(currentTask)}</Descriptions.Item>
                <Descriptions.Item label="Description">
                  {currentTask.description || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Room Number">
                  {currentTask.roomNumber || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Status">{statusTag(currentTask.status)}</Descriptions.Item>
                <Descriptions.Item label="Assigned At">
                  {currentTask.assignedAt ? new Date(currentTask.assignedAt).toLocaleString() : "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Started At">
                  {currentTask.startedAt ? new Date(currentTask.startedAt).toLocaleString() : "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Completed At">
                  {currentTask.completedAt ? new Date(currentTask.completedAt).toLocaleString() : "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Notes">{currentTask.notes || "N/A"}</Descriptions.Item>
              </Descriptions>

              <Divider />

              <Title level={5} style={{ marginTop: 0 }}>
                Checklist Progress
              </Title>

              {(() => {
                const { done, total, percent } = calcChecklistProgress(currentTask);
                return (
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Progress percent={percent} />
                    <Text type="secondary">
                      {done}/{total} items completed
                    </Text>
                  </Space>
                );
              })()}

              <Divider />

              <Title level={5} style={{ marginTop: 0 }}>
                Checklist Items (Read-only)
              </Title>

              <List
                bordered
                dataSource={Array.isArray(currentTask.checklistItems) ? currentTask.checklistItems : []}
                locale={{ emptyText: "No checklist items for this task" }}
                renderItem={(item) => (
                  <List.Item>
                    <Space style={{ width: "100%", justifyContent: "space-between" }}>
                      <Space>
                        <input type="checkbox" checked={!!item.isCompleted} readOnly />
                        <span
                          style={{
                            textDecoration: item.isCompleted ? "line-through" : "none",
                            opacity: item.isCompleted ? 0.7 : 1,
                          }}
                        >
                          {item.description}
                        </span>
                      </Space>
                      {item.isCompleted && item.completedAt ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {new Date(item.completedAt).toLocaleString()}
                        </Text>
                      ) : null}
                    </Space>
                  </List.Item>
                )}
              />
            </>
          ) : (
            <Alert type="warning" message="No task loaded" showIcon />
          )}
        </Modal>
      </Card>
    </div>
  );
};

export default HousekeeperDashboard;
